import React, { useEffect, useState } from "react";
import "./ParticipantEngagementRisk.css";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import { EngagementRiskDto } from "../../../interfaces/participant/ParticipantEngagementRisk";
import { getEngagementRiskReport } from "../../../services/participantServices/ParticipantEngagementRiskService";
import { useAuth } from "../../../firebase/AuthProvider";

interface ParticipantEngagementRiskProps {
  onLogActivityClick?: (participantId: number) => void; // Updated ID to number to match API
}

const ParticipantEngagementRisk: React.FC<ParticipantEngagementRiskProps> = ({ 
  onLogActivityClick 
}) => {
  const { currentUser } = useAuth();
  const [riskData, setRiskData] = useState<EngagementRiskDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchRiskData = async () => {
      try {
        setLoading(true);
        if (!currentUser) return;
        const token = await currentUser.getIdToken()
        // Fetch top 3 highest risk participants
        const response = await getEngagementRiskReport(token, {
          pageNumber: 1,
          pageSize: 3,
          sortColumn: "daysSinceLastActivity",
          sortDirection: "desc"
        });

        if (response.success && response.data) {
          setRiskData(response.data.items);
        }
      } catch (error) {
        console.error("Failed to load engagement risk data", error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchRiskData();
    }
  }, [currentUser]);

  const getRiskColor = (riskLevel: string) => {
    // These all appeared to be the same gray in your original code, 
    // but you can customize them here if needed.
    return "text-[#6A7282]";
  };

  const getRiskDotColor = (riskLevel: string) => {
    // Normalize string case just to be safe
    switch (riskLevel?.toLowerCase()) {
      case "high risk":
        return "bg-[#FF6467]";
      case "at risk":
        return "bg-[#FF8904]";
      case "watch":
        return "bg-[#F0B100]";
      case "low risk":
        return "bg-[#2ECC71]"; // Added a green for low risk just in case
      default:
        return "bg-[#6A7282]";
    }
  };

  const formatActivityText = (days: number | null) => {
    if (days === null || days === undefined) return "No activity logged";
    if (days === 0) return "Today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  };

  if (loading) {
    return (
      <div className="common_card top !mb-0 h-full flex items-center justify-center">
        <p className="text-gray-400">Loading risks...</p>
      </div>
    );
  }

  return (
    <div className="common_card top !mb-0 h-full">
      <div className="ow_nw_header_row">
        <h3 className="text-[16px] xl:text-[18px] 2xl:text-[20px] font-semibold">
          Participant Engagement Risk
        </h3>
      </div>

      <div className="flex flex-col gap-[0.63rem] px-3">
        {riskData.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm">
            No participants found at risk.
          </div>
        ) : (
          riskData.map((participant) => (
            <div
              key={participant.participantId}
              className="flex items-center w-full py-[1.06rem] pl-[0.5rem] pr-[0.5rem] bg-[#F4F4F4] justify-between rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-[0.88rem]">
                <div className="relative min-w-[2.75rem] min-h-[2.75rem] justify-center rounded-[3.75rem] border sm:min-w-[3.75rem] sm:min-h-[3.75rem] participant-left flex items-center gap-[0.88rem] flex-1">
                  {/* Using AvatarImage with the participant ID to generate/fetch the avatar */}
                  <AvatarImage
                    userId={participant.participantId.toString()}
                    className="w-[3.75rem] object-cover h-[3.75rem] rounded-[3.75rem]"
                    size={"sm"}
                  />
                </div>

                <div className="participant-info">
                  <p className="participant-name line-clamp-1">
                    {participant.firstName} {participant.lastName}
                  </p>

                  <div className="participant-risklevel">
                    <span
                      className={`risk-dot ${getRiskDotColor(participant.riskLevel)}`}
                    />
                    <span
                      className={`risk-text ${getRiskColor(participant.riskLevel)}`}
                    >
                      {participant.riskLevel}
                    </span>
                  </div>
                </div>
              </div>

              <div className="participant-activity-container ml-auto">
                {/* Last Activity */}
                <div className="last-activity ml-auto">
                  <div className="flex flex-wrap items-center ml-auto justify-end gap-1">
                    <span className="last-activity-text flex items-center gap-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        className="last-activity-icon"
                      >
                        <g clipPath="url(#clip0)">
                          <path
                            d="M6.99984 12.8333C10.2215 12.8333 12.8332 10.2217 12.8332 7.00001C12.8332 3.77834 10.2215 1.16667 6.99984 1.16667C3.77818 1.16667 1.1665 3.77834 1.1665 7.00001C1.1665 10.2217 3.77818 12.8333 6.99984 12.8333Z"
                            stroke="#99A1AF"
                            strokeWidth="1.16667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7 3.5V7L9.33333 8.16667"
                            stroke="#99A1AF"
                            strokeWidth="1.16667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                      </svg>
                      Last activity
                    </span>
                    <span className="last-activity-dynamic">
                      {formatActivityText(participant.daysSinceLastActivity)}
                    </span>
                  </div>
                </div>

                {/* Log Activity Button */}
                <button
                  className="log-activity-btn ml-auto items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLogActivityClick?.(participant.participantId);
                  }}
                >
                  <span>Log activity</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="10"
                    viewBox="0 0 8 14"
                    fill="none"
                  >
                    <path
                      d="M0.880504 0L8 7L0.880504 14L0 13.1219L6.22642 7L0 0.878092L0.880504 0Z"
                      fill="black"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ParticipantEngagementRisk;